import { useException, useGet, useLoading, useModal } from "hooks"
import { CadastroSegurancaItensSubItensServices, ICreateSubItemSegurancaBody } from "services/GST/CadastroItensSubitensSeguranca"
import { Form, Grid, Input, Button, Card, Row, Title, Select } from 'components'
import { useForm, useFieldArray } from "react-hook-form"
import { IFormSecurityItem } from "../Search/components/SecurityItem/types"
import * as yup from 'yup'
import { IEntityGST, IResponseTypes } from "services/core/genericEntityGST"
import { SPACING } from "utils"
import AddIcon from '@mui/icons-material/Add'
import Remove from '@mui/icons-material/Remove'
import { yupResolver } from "@hookform/resolvers/yup"
import { IGetInfracoesResponse } from "services/GST/Dicionarios"

const SCHEMA = yup.object({
    txDescricaoItemSeguranca: yup.string().typeError('Campo obrigatório.').required('Campo obrigatório.'),
    nmItemSeguranca: yup.string().typeError('Campo obrigatório.').required('Campo obrigatório.'),
    txLegislacaoItemSeguranca: yup.string().typeError('Campo obrigatório.').required('Campo obrigatório.'),
    listaItensSeguranca: yup.array().of(
        yup.object().shape(
            {
                nmSubItemSeguranca: yup.string().typeError('Adicione um subitem de segurança').required('Adicione um subitem de segurança'),
                txSubItemDescricao: yup.string().typeError('Adicione uma descrição do subitem').required('Adicione uma descrição do subitema'),
                idInfracaoSubItemSeguranca: yup.string().typeError('Adicione uma infração').required('Adicione uma infração'),
                idSubItemSeguranca: yup.number().default(0).notRequired(),
                itemSegurancaId: yup.number().default(0).notRequired(),
            })).required()
})



const AddItemSecurity: React.FC = () => {
    const { showModal } = useModal()
    const { setLoading } = useLoading()
    const { createCustomException, getException } = useException()

    const {
        control,
        reset,
        formState: { errors },
        watch,
        setValue,
        handleSubmit
    } = useForm({
        defaultValues: {
            nmItemSeguranca: '',
            txLegislacaoItemSeguranca: '',
            txDescricaoItemSeguranca: '',
            listaItensSeguranca: [{ nmSubItemSeguranca: null, txSubItemDescricao: null, idInfracaoSubItemSeguranca: null, idSubItemSeguranca: 0, itemSegurancaId: 0 }]
        }, resolver: yupResolver(SCHEMA)
    })

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'listaItensSeguranca'
    })

    const subItemSegurancaForInfracao = watch('listaItensSeguranca');

    const handleInfracao = (value, index) => {
        setValue(`listaItensSeguranca.${index}.idInfracaoSubItemSeguranca`, value)
    }

    const handleSubmitForm = (form: IFormSecurityItem) => {
        setLoading('loading')

        CadastroSegurancaItensSubItensServices()
            .createItemSegurancaWithSubItem({
                idItemSeguranca: 0,
                nmItemSeguranca: form.nmItemSeguranca,
                txDescricaoItemSeguranca: form.txDescricaoItemSeguranca,
                txLegislacaoItemSeguranca: form.txLegislacaoItemSeguranca,
                listaItensSeguranca: form.listaItensSeguranca
            })
            .then((response) => {
                if (response.status !== 200) {
                    const { mensagem, detalhe, tipo } = response.data

                    createCustomException({
                        detalhe,
                        mensagem,
                        tipo: IResponseTypes[tipo]
                    })
                }

                showModal({
                    hideButtonCancel: true,
                    message: 'Item criado com sucesso!',
                    txtButtonConfirm: 'OK',
                    type: 'success'
                })
                reset({
                    nmItemSeguranca: '',
                    txLegislacaoItemSeguranca: '',
                    txDescricaoItemSeguranca: '',
                    listaItensSeguranca: [{ nmSubItemSeguranca: null, txSubItemDescricao: null, idInfracaoSubItemSeguranca: null, idSubItemSeguranca: 0, itemSegurancaId: 0 }]
                })
                setLoading('idle')
            })
            .catch((err: Error) => {
                setLoading('idle')
                const error = getException(err)

                showModal({
                    hideButtonCancel: true,
                    message: error.mensagem || 'Não foi possível criar o novo item',
                    txtButtonConfirm: 'OK',
                    type: error.tipo || 'error'
                })
            })
    }

    const { data } = useGet<{}, IEntityGST<IGetInfracoesResponse[]>>({
        api: 'Gst',
        params: {},
        url: 'Infracao/GetInfracaoToDictionary'
    })


    const addSubItem = () => {
        const newSubItem: ICreateSubItemSegurancaBody = {
            txSubItemDescricao: null,
            idInfracaoSubItemSeguranca: null,
            nmSubItemSeguranca: null,
            idSubItemSeguranca: 0,
            itemSegurancaId: 0
        }

        append(newSubItem)
    }

    const rmvSubItem = (index: number) => {
        remove(index)
    }

    return (
        <Card
            marginLeft={SPACING.spacing20}
            marginRight={SPACING.spacing20}
            marginBottom={SPACING.spacing100}
            padding={SPACING.spacing10}
            boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
            borderRadius={`0px 10px 0px 0px`}>
            <Form
                onSubmit={handleSubmit(handleSubmitForm)}
                marginBottom={SPACING.spacing10}
                padding={SPACING.spacing20}
            >
                <Grid
                    gridCols="11fr 1fr"
                    gridGap={`0 ${SPACING.spacing20}`}
                >
                    <Title fontsize="16px">ITEM SEGURANÇA</Title>
                </Grid>
                <Grid
                    gridCols="3fr 3fr 5fr 1fr"
                    gridGap={`0 ${SPACING.spacing20}`}
                    margin={`${SPACING.spacing20} 0 ${SPACING.spacing10} 0`}
                >
                    <Input
                        control={control}
                        errors={errors}
                        label="Item"
                        placeholder="Item"
                        formValue="nmItemSeguranca"
                        required
                    />
                    <Input
                        control={control}
                        errors={errors}
                        label="Legislação"
                        placeholder="Legislação"
                        formValue="txLegislacaoItemSeguranca"
                        required
                    />
                    <Input
                        control={control}
                        errors={errors}
                        label="Descrição do item"
                        placeholder="Descrição"
                        formValue="txDescricaoItemSeguranca"
                        required
                    />
                </Grid>
                <>
                    <Grid
                        gridCols="11fr 1fr"
                        gridGap={`0 ${SPACING.spacing20}`}
                        margin={`${SPACING.spacing50} 0 ${SPACING.spacing10} 0`}
                    >
                        <Title fontsize="16px">SUBITEMS DE SEGURANÇA</Title>
                    </Grid>
                    {fields.map((_, index) => {
                        const field = `listaItensSeguranca.[${index}]`;
                        return (
                            <Grid
                                gridCols="3fr 3fr 5fr 1fr 1fr 1fr"
                                gridGap={`${SPACING.spacing20} ${SPACING.spacing20}`}
                                margin={`${SPACING.spacing20} 0 ${SPACING.spacing10} 0`}
                            >
                                <div>
                                    <Input
                                        control={control}
                                        errors={errors}
                                        label="Subitem de Segurança"
                                        placeholder="Subitem de Segurança"
                                        formValue={`${field}.nmSubItemSeguranca`}
                                        customError={errors?.listaItensSeguranca?.[index]?.nmSubItemSeguranca?.message}
                                        required
                                    />
                                </div>
                                <div>
                                    <Input
                                        control={control}
                                        errors={errors}
                                        label="Descrição do SubItem"
                                        placeholder="Descrição do SubItem"
                                        formValue={`${field}.txSubItemDescricao`}
                                        customError={errors?.listaItensSeguranca?.[index]?.txSubItemDescricao?.message}
                                        required
                                    />
                                </div>
                                <div>
                                    <Select
                                        listOptions={data?.entity?.map(infracao => ({
                                            label: infracao.nuNivelInfracao,
                                            value: infracao.idInfracao,
                                        })) || []}
                                        errors={errors}
                                        onChange={(value) => handleInfracao(value.target.value, index)}
                                        label="Infração"
                                        customError={errors?.listaItensSeguranca?.[index]?.idInfracaoSubItemSeguranca?.message}
                                        value={subItemSegurancaForInfracao[index]['idInfracaoSubItemSeguranca']}
                                        required
                                    />
                                </div>
                                <Button
                                    variantButton="default"
                                    text=""
                                    startIcon={<AddIcon />}
                                    onClick={() => addSubItem()}
                                />
                                {index !== 0 && (
                                    <Button
                                        variantButton="default"
                                        text={''}
                                        startIcon={<Remove />}
                                        onClick={() => rmvSubItem(index)}

                                    />
                                )}
                            </Grid>
                        )
                    })}
                </>
                <Grid
                    gridCols="5fr 5fr"
                    gridGap={`0 ${SPACING.spacing20}`}
                    margin={`${SPACING.spacing20} 0 ${SPACING.spacing10} 0`}
                >
                </Grid>
                <Row justifyContent={'flex-end'}>
                    <Button
                        variantButton="default"
                        text={'ADICIONAR'}
                        type="submit"
                        marginTop={SPACING.spacing50}
                    />
                </Row>
            </Form>
        </Card>
    )
}

export default AddItemSecurity